import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import { IDog } from "@eagerdog/interfaces";
import { Config } from "@eagerdog/config";
import { isDogProfileOwner, canOpenOwnDogProfile, DOG_OWNER_PREMIUM_ENABLED } from "@eagerdog/constants";

import { apiService } from "../../services/api.service";
import { helperService } from "../../services/helper.service";
import { toast } from "../../components/Toast/ToastManager";

import TabContent, { ITab, useTabContent } from "../../components/TabContent/TabContent";
import DogPortrait from "../../components/DogPortrait/DogPortrait";
import RibbonRoomTab from "./RibbonRoomTab/RibbonRoomTab";
import StatsTab from "./StatsTab/StatsTab";
import PremiumGate from "./PremiumGate/PremiumGate";
import { Modal, useModal } from "../../components/Modal/Modal";
import ShareModal from "../../components/ShareModal/ShareModal";
import DogForm from "../../components/StepForm/DogForm/DogForm";
import Confirm from "../../components/Confirm/Confirm";

import eyeIcon from "../../assets/icons/eye.svg";
import eyeOffIcon from "../../assets/icons/eye_off.svg";

import "./DogProfile.scss";

interface IProps {}

const DogProfile: React.FC<IProps> = (props) => {
  const { dogId } = useParams();
  const navigate = useNavigate();
  const { setTab, activeTab } = useTabContent();
  const shareModal = useModal();
  const editModal = useModal();
  const makePublicConfirmModal = useModal();
  
  const user = helperService.getUser();
  const isLoggedIn = helperService.isLoggedIn();
  const [dog, setDog] = useState<IDog>();
  const [loading, setLoading] = useState<boolean>(true);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  // Owners need the Dog Profile (Premium) feature for tracker data; public viewers always see it.
  // Dog Owner Premium is retired (DOG_OWNER_PREMIUM_ENABLED=false): tracker data is free for everyone.
  const trackerAccess = !isOwner || !DOG_OWNER_PREMIUM_ENABLED || canOpenOwnDogProfile(user);

  const tabs: ITab[] = [
    { name: "Ribbon Room", actions: [] },
    { name: "Stats", actions: [] },
    { name: "Information", actions: [] }
  ];

  useEffect(() => {
    if (dogId) {
      // Try to load the dog - either as authenticated user or as public
      const loadDog = async () => {
        try {
          let dogData: IDog;
          
          if (isLoggedIn) {
            // If logged in, try the authenticated endpoint first
            try {
              dogData = await apiService.getDog(dogId);
              // Ownership / feature access use the shared helpers (same rules as mobile)
              const userIsOwner = isDogProfileOwner(user, dogData);
              setIsOwner(userIsOwner);

              // Owners without the Premium feature still get in; the Ribbon Room and
              // Stats tabs show an upsell instead of data (Information stays free).

              // If not owner and dog is not public, deny access
              if (!userIsOwner && !dogData.is_public) {
                toast.show({
                  title: "Dog Profile",
                  content: "You don't have permission to view this dog's profile",
                  duration: 10000,
                  type: "fail"
                });
                navigate("/account?t=0");
                return;
              }
            } catch (e: any) {
              // If authenticated call fails, try public endpoint
              dogData = await apiService.getPublicDog(dogId);
              setIsOwner(false);
            }
          } else {
            // Not logged in - use public endpoint
            dogData = await apiService.getPublicDog(dogId);
            setIsOwner(false);
          }
          
          setDog(dogData);
          setLoading(false);
        } catch (e: any) {
          toast.show({
            title: "Dog Profile",
            content: e.response?.status === 403 
              ? "This dog profile is private" 
              : "Unable to load dog information",
            duration: 10000,
            errorDetails: e,
            type: "fail"
          });
          if (isLoggedIn) {
            navigate("/account?t=0");
          } else {
            navigate("/login?r=/dogs/" + dogId);
          }
        }
      };
      
      loadDog();
    }
  }, [dogId, isLoggedIn]);

  const togglePublicStatus = async () => {
    if (!dog || !dogId) return;

    const newPublicStatus = !dog.is_public;

    try {
      await apiService.updateDog(dogId, { is_public: newPublicStatus });
      
      setDog({ ...dog, is_public: newPublicStatus });
      
      toast.show({
        title: newPublicStatus ? "Profile is now Public" : "Profile is now Private",
        content: newPublicStatus 
          ? "Others can now view your dog's profile when you share the link" 
          : "Your dog's profile is now private",
        duration: 5000,
        type: "success"
      });
    } catch (e: any) {
      toast.show({
        title: "Error",
        content: "Failed to update profile visibility",
        duration: 5000,
        errorDetails: e,
        type: "fail"
      });
    }
  };

  const handleShareClick = () => {
    if (!dog?.is_public) {
      // Show confirmation modal to make public first
      makePublicConfirmModal.toggle();
    } else {
      // Profile is already public, show share modal
      shareModal.toggle();
    }
  };

  const handleMakePublicAndShare = async () => {
    makePublicConfirmModal.toggle();
    
    if (!dog || !dogId) return;

    try {
      await apiService.updateDog(dogId, { is_public: true });
      setDog({ ...dog, is_public: true });
      
      toast.show({
        title: "Profile is now Public",
        content: "Others can now view your dog's profile",
        duration: 5000,
        type: "success"
      });
      
      // Now show the share modal
      shareModal.toggle();
    } catch (e: any) {
      toast.show({
        title: "Error",
        content: "Failed to make profile public",
        duration: 5000,
        errorDetails: e,
        type: "fail"
      });
    }
  };

  const renderInformation = () => {
    if (!dog) return null;
    
    return (
      <div className="dogInfoTab">
        <div className="infoSection">
          <h3>Basic Information</h3>
          <div className="infoGrid">
            <div className="infoItem">
              <span className="label">Call Name</span>
              <span className="value">{dog.call_name}</span>
            </div>
            {dog.registered_name && (
              <div className="infoItem">
                <span className="label">Registered Name</span>
                <span className="value">
                  {dog.prefix_title && `${dog.prefix_title} `}
                  {dog.registered_name}
                  {dog.suffix_title && ` ${dog.suffix_title}`}
                </span>
              </div>
            )}
            <div className="infoItem">
              <span className="label">Breed</span>
              <span className="value">{dog.breed}{dog.variety && ` - ${dog.variety}`}</span>
            </div>
            <div className="infoItem">
              <span className="label">Sex</span>
              <span className="value">{dog.sex}</span>
            </div>
            <div className="infoItem">
              <span className="label">Date of Birth</span>
              <span className="value">{dog.dob ? new Date(dog.dob).toLocaleDateString() : "—"}</span>
            </div>
            {dog.height_at_withers && (
              <div className="infoItem">
                <span className="label">Height at Withers</span>
                <span className="value">{dog.height_at_withers}"</span>
              </div>
            )}
          </div>
        </div>

        {dog.club_registrations && dog.club_registrations.length > 0 && (
          <div className="infoSection">
            <h3>Registered Clubs</h3>
            <div className="clubTags">
              {dog.club_registrations.map((reg, idx) => (
                <span key={idx} className="clubTag">{reg.sanctioning_club}</span>
              ))}
            </div>
          </div>
        )}

        <div className="infoActions">
          <Link to="/account?t=0" className="button secondary">← Back to My Dogs</Link>
        </div>
      </div>
    );
  };

  if (loading || !dog) {
    return (
      <div className="dogProfilePage">
        <div className="container">
          <div className="loading">Loading dog profile...</div>
        </div>
      </div>
    );
  }

  // Build SEO-friendly title and description
  const fullName = dog.registered_name 
    ? `${dog.prefix_title ? dog.prefix_title + ' ' : ''}${dog.registered_name}${dog.suffix_title ? ' ' + dog.suffix_title : ''}`
    : dog.call_name;
  
  const pageTitle = `${dog.call_name} - ${dog.breed} | Eagerdog`;
  const pageDescription = dog.registered_name
    ? `Meet ${dog.call_name} (${fullName}), a talented ${dog.breed}${dog.variety ? ' - ' + dog.variety : ''} competing in dog sports.`
    : `Meet ${dog.call_name}, a talented ${dog.breed}${dog.variety ? ' - ' + dog.variety : ''} competing in dog sports.`;
  
  const pageUrl = `${window.location.origin}/dogs/${dogId}`;
  const apiBase = Config.web.REACT_APP_API_URL || window.location.origin;
  const previewUrl = `${apiBase}/preview/dog/${dogId}`;
  const ogImageUrl = `${apiBase}/preview/dog/${dogId}/og-image.png`;

  return (
    <div className="dogProfilePage">
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={fullName || dog.call_name} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${dog.call_name} - ${dog.breed}`} />
        <meta property="og:site_name" content="Eager Dog" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={fullName || dog.call_name} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImageUrl} />
        <meta name="twitter:image:alt" content={`${dog.call_name} - ${dog.breed}`} />
        
        {/* Additional Meta Tags */}
        <link rel="canonical" href={pageUrl} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="container">
        <div className="dogProfileHeader">
          <div className="dogImageWrapper">
            <DogPortrait dog={dog} showName={false} className="size-profile" />
          </div>
          <div className="dogHeaderInfo">
            <h1>{dog.call_name}</h1>
            {dog.registered_name && (
              <h2 className="registeredName">
                {dog.prefix_title && `${dog.prefix_title} `}
                {dog.registered_name}
                {dog.suffix_title && ` ${dog.suffix_title}`}
              </h2>
            )}
            <div className="dogBreed">{dog.breed}{dog.variety && ` - ${dog.variety}`}</div>
            {isOwner && (
              <Link to="/account?t=0" className="backLink">← Back to My Dogs</Link>
            )}
          </div>
          {isOwner && (
            <div className="actions">
              <div className="btnWrap">
                <button 
                  onClick={editModal.toggle} 
                  className="edit"
                  title="Edit Dog"
                >
                  Edit Dog
                </button>
              </div>
              <div className="btnWrap">
                <button 
                  onClick={togglePublicStatus} 
                  className="visibility"
                  title={dog.is_public ? "Make Private" : "Make Public"}
                >
                  <img 
                    src={dog.is_public ? eyeIcon : eyeOffIcon} 
                    alt={dog.is_public ? "Public" : "Private"} 
                  />
                </button>
              </div>
              <div className="btnWrap">
                <button 
                  onClick={handleShareClick} 
                  className="share"
                  title="Share Profile"
                >
                  Share Profile
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="dogProfileContent">
          <TabContent setTab={setTab} activeTab={activeTab} tabs={tabs}>
            {trackerAccess ? (
              <RibbonRoomTab dog={dog} isOwner={isOwner} />
            ) : (
              <PremiumGate
                dogName={dog.call_name}
                feature="Ribbon Room"
                bullets={[
                  "Track titles and legs across every sport",
                  "See exactly what's needed for the next title",
                  "Add past runs from other clubs and events",
                ]}
              />
            )}
            {trackerAccess ? (
              <StatsTab dog={dog} isOwner={isOwner} />
            ) : (
              <PremiumGate
                dogName={dog.call_name}
                feature="Stats"
                bullets={[
                  "Lifetime events, runs, and Q rate",
                  "Personal bests and Q streaks",
                  "Entry fees paid, per run and per Q",
                ]}
              />
            )}
            {renderInformation()}
          </TabContent>
        </div>
      </div>

      {/* Share Modal */}
      {shareModal.isShown && (
        <Modal
          isShown={shareModal.isShown}
          hide={shareModal.toggle}
          className="ShareModalContainer"
          modalContent={
            <ShareModal
              eventTitle={fullName || dog.call_name}
              eventUrl={previewUrl}
              shareType="dog"
              onClose={shareModal.toggle}
            />
          }
        />
      )}

      {/* Edit Dog Modal */}
      <Modal
        className="dogForm"
        isShown={editModal.isShown}
        hide={editModal.toggle}
        modalContent={
          <DogForm 
            onFinish={(updatedDog) => { 
              if (updatedDog) {
                setDog(updatedDog);
              }
              editModal.toggle();
            }}
            onDogUpdated={(updatedDog) => setDog(updatedDog)}
            dog={dog} 
          />
        }
      />

      {/* Make Public Confirmation Modal */}
      <Modal
        className="Confirmation"
        isShown={makePublicConfirmModal.isShown}
        hide={makePublicConfirmModal.toggle}
        modalContent={
          <Confirm
            message={
              <>
                <p>In order for others to see your dog's page, you need to make it public.</p>
                <p>Would you like to do that now?</p>
              </>
            }
            onConfirm={handleMakePublicAndShare}
            onCancel={makePublicConfirmModal.toggle}
          />
        }
      />
    </div>
  );
};

export default DogProfile;

